// Tally without an account: the demo's in-memory server, empty, in this browser only. Every accepted change is saved in
// localStorage. Signing in later takes the guest's projects, clients and entries into the account (`guestChanges`);
// Pro, sync, teams, devices and Stripe need an account.
import { ApiError, type LiveHandlers } from './client.ts';
import { DemoApi } from './demo.ts';
import type {
  Base, Change, Client, Me, Project, ProjectRule, Role, Setting, StripeStatus, TeamResponse, TimeEntry, TimeEntryVersion,
} from './types.ts';

/** The guest's workspace. */
const DATA_KEY = 'tally.guest';
/** `guest` while this browser runs without an account. */
const MODE_KEY = 'tally.mode';

export interface GuestState {
  format: 1;
  workspaceId: string;
  memberId: string;
  seq: number;
  projects: Project[];
  clients: Client[];
  projectRules: ProjectRule[];
  settings: Setting[];
  entries: TimeEntry[];
  versions: TimeEntryVersion[];
}

/** Where the guest's data lives: localStorage, or nothing (the demo's guest, tests). */
export interface GuestStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function browserStorage(): GuestStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function guestMode(storage = browserStorage()): boolean {
  try {
    return storage?.getItem(MODE_KEY) === 'guest';
  } catch {
    return false;
  }
}

export function setGuestMode(on: boolean, storage = browserStorage()): void {
  try {
    if (on) storage?.setItem(MODE_KEY, 'guest');
    else storage?.removeItem(MODE_KEY);
  } catch { /* private mode: the next load asks again */ }
}

export function loadGuest(storage = browserStorage()): GuestState | null {
  try {
    const raw = storage?.getItem(DATA_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as Partial<GuestState>;
    if (state.format !== 1 || typeof state.workspaceId !== 'string' || typeof state.memberId !== 'string') return null;
    return {
      format: 1, workspaceId: state.workspaceId, memberId: state.memberId, seq: Number(state.seq) || 1,
      projects: state.projects ?? [], clients: state.clients ?? [], projectRules: state.projectRules ?? [], settings: state.settings ?? [],
      entries: state.entries ?? [], versions: state.versions ?? [],
    };
  } catch {
    return null;
  }
}

async function withGuestLock<T>(storage: GuestStorage | null, action: () => Promise<T>): Promise<T> {
  if (!storage) return action();
  if (!globalThis.navigator?.locks) throw new ApiError(0, 'guest_storage');
  try { return await navigator.locks.request(DATA_KEY, action); }
  catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, 'guest_storage');
  }
}

/** Import may finish after another tab saves: only remove the snapshot that actually reached the account. */
export function clearGuest(expected: GuestState, storage: GuestStorage | null = browserStorage()): Promise<boolean> {
  return withGuestLock(storage, async () => {
    const unchanged = JSON.stringify(loadGuest(storage)) === JSON.stringify(expected);
    if (unchanged) storage?.removeItem(DATA_KEY);
    storage?.removeItem(MODE_KEY);
    return unchanged;
  });
}

/**
 * Switches this browser into guest mode or out of it (to sign in or create an account). The API is chosen at load, so
 * the page loads again. The demo keeps its flags: `?demo&guest` is the demo's guest, `?demo&signedout` its sign-in.
 */
export function switchGuest(on: boolean): void {
  const params = new URLSearchParams(window.location.search);
  if (params.has('demo')) {
    params.delete(on ? 'signedout' : 'guest');
    params.set(on ? 'guest' : 'signedout', '');
  } else {
    setGuestMode(on);
  }
  const query = params.toString().replace(/=(&|$)/g, '$1');
  window.location.assign('/' + (query ? `?${query}` : ''));
}

/** Anything a guest created: worth taking into an account. */
export function guestHasData(state: GuestState | null): boolean {
  return !!state && (state.projects.length + state.clients.length + state.entries.length) > 0;
}

/**
 * The guest's records as changes for an account, in an order the server accepts (clients before projects before
 * entries). Entries move to the account's member. A new workspace (claimed with the guest's ids) also takes the
 * settings; an existing one keeps its own.
 */
export function guestChanges(state: GuestState, memberId: string, withSettings: boolean): Change[] {
  const changes: Change[] = [];
  for (const data of state.clients) changes.push({ entity: 'clients', op: 'upsert', data });
  for (const data of state.projects) changes.push({ entity: 'projects', op: 'upsert', data });
  for (const data of state.projectRules) changes.push({ entity: 'projectRules', op: 'upsert', data });
  if (withSettings) for (const data of state.settings) changes.push({ entity: 'settings', op: 'upsert', data });
  for (const entry of state.entries) changes.push({ entity: 'timeEntries', op: 'upsert', data: { ...entry, memberId } });
  for (const version of state.versions) {
    const snapshot = 'memberId' in version.snapshot ? { ...version.snapshot, memberId } : version.snapshot;
    changes.push({ entity: 'timeEntryVersions', op: 'upsert', data: { ...version, snapshot } });
  }
  return changes;
}

/** Answers like the server, from this browser's storage. */
export class GuestApi extends DemoApi {
  override readonly demo = false;
  override readonly guest = true;
  private readonly storage: GuestStorage | null;
  private workspaceId: string;
  private memberId: string;
  private initialized: boolean;
  private ended = false;

  constructor(clock: () => number, timeZone: string, storage: GuestStorage | null = browserStorage()) {
    super(clock, timeZone, { signedIn: true, empty: true });
    this.storage = storage;
    const state = loadGuest(storage);
    this.workspaceId = state?.workspaceId ?? crypto.randomUUID();
    this.memberId = state?.memberId ?? crypto.randomUUID();
    this.initialized = state !== null;
    this.load(state);
  }

  private load(state: GuestState | null): void {
    this.workspaceId = state?.workspaceId ?? this.workspaceId;
    this.memberId = state?.memberId ?? this.memberId;
    this.seq = state?.seq ?? 1;
    this.data = {
      ...this.data,
      base: {
        seq: this.seq, projects: state?.projects ?? [], clients: state?.clients ?? [], projectRules: state?.projectRules ?? [],
        settings: state?.settings ?? [], members: [{ id: this.memberId, name: '' }],
      },
      entries: state?.entries ?? [], versions: state?.versions ?? [], devices: [],
    };
  }

  private current<T>(action: () => Promise<T>): Promise<T> {
    return withGuestLock(this.storage, async () => {
      if (this.ended) throw new ApiError(401, 'guest_changed');
      if (this.storage) {
        const state = loadGuest(this.storage);
        if (this.initialized && (!state || state.workspaceId !== this.workspaceId || state.memberId !== this.memberId)) {
          this.ended = true;
          throw new ApiError(401, 'guest_changed');
        }
        if (state) this.load(state);
        else {
          if (this.storage.getItem(DATA_KEY) !== null) throw new ApiError(0, 'guest_storage');
          this.saved();
        }
        this.initialized = true;
      }
      return action();
    });
  }

  /** Every writer reloads the latest snapshot under the origin-wide lock before changing it. */
  override push(changes: Change[]) { return this.current(() => super.push(changes)); }

  /** Storage failures are reported to the store instead of silently claiming that the change was saved. */
  protected override saved(): void {
    const d = this.data;
    const state: GuestState = {
      format: 1, workspaceId: this.workspaceId, memberId: this.memberId, seq: this.seq,
      projects: d.base.projects, clients: d.base.clients, projectRules: d.base.projectRules, settings: d.base.settings,
      entries: d.entries, versions: d.versions,
    };
    if (!this.storage) return;
    try {
      this.storage.setItem(DATA_KEY, JSON.stringify(state));
    } catch {
      throw new ApiError(0, 'guest_storage');
    }
  }

  override async me(): Promise<Me | null> {
    if (this.ended) return this.later(null);
    return this.current(() => this.later({
      user: { id: 'guest', email: null, name: '' },
      workspace: { id: this.workspaceId, name: 'Tellary', memberId: this.memberId, memberName: '', role: 'owner', personal: true },
      devices: [],
      // Guests never have Pro: it belongs to an account (spec/pro.md).
      pro: { active: false, source: null, until: null },
    }));
  }

  override base(): Promise<Base> {
    return this.current(() => super.base());
  }

  override range(start: number, end: number, usage: boolean) { return this.current(() => super.range(start, end, usage)); }
  override session(id: string) { return this.current(() => super.session(id)); }
  override events(id: string) { return this.current(() => super.events(id)); }
  override versions(id: string) { return this.current(() => super.versions(id)); }
  override tags() { return this.current(() => super.tags()); }

  override live(handlers: LiveHandlers): () => void {
    const stop = super.live(handlers);
    const changed = (event: StorageEvent) => {
      if (event.storageArea === this.storage && (event.key === DATA_KEY || event.key === null)) handlers.onChange(0);
    };
    if (typeof window !== 'undefined') window.addEventListener('storage', changed);
    return () => {
      stop();
      if (typeof window !== 'undefined') window.removeEventListener('storage', changed);
    };
  }

  /** Leaving guest mode keeps the data: signing in takes it into the account, "Als Gast fortfahren" brings it back. */
  override async logout(): Promise<void> {
    await withGuestLock(this.storage, async () => { setGuestMode(false, this.storage); });
  }

  override async team(): Promise<TeamResponse> {
    return this.later({ role: 'owner', members: [], invites: [] });
  }

  override async workspaces() { return { workspaces: [], activeWorkspaceId: null, pendingInvites: [] }; }
  override async createWorkspace(): Promise<never> { throw new ApiError(403, 'account_required'); }
  override async selectWorkspace(): Promise<never> { throw new ApiError(403, 'account_required'); }
  override async acceptInvite(): Promise<never> { throw new ApiError(403, 'account_required'); }
  override async declineInvite(): Promise<never> { throw new ApiError(403, 'account_required'); }
  override async renameWorkspace(): Promise<never> { throw new ApiError(403, 'account_required'); }
  override async leaveWorkspace(): Promise<never> { throw new ApiError(403, 'account_required'); }
  override async invite(_email: string, _role: Role): Promise<never> { throw new ApiError(403, 'account_required'); }
  /** Pro belongs to an account: a guest has none to attach it to. */
  override async redeemProCode(): Promise<never> { throw new ApiError(403, 'account_required'); }
  override async addDevice(_name: string, _platform: string): Promise<{ id: string; token: string }> { throw new ApiError(403, 'account_required'); }
  override async stripe(): Promise<StripeStatus> { return this.later({ available: false, connected: false }); }
  override async connectStripe(): Promise<StripeStatus> { throw new ApiError(403, 'account_required'); }
  override async invoices() { return this.later([]); }
  override async createInvoice(): Promise<never> { throw new ApiError(403, 'account_required'); }

  override async exportAccount(): Promise<Blob> {
    return this.current(async () => {
      const d = this.data;
      const payload = { format: 'tally-guest-export', data: { ...d.base, timeEntries: d.entries, timeEntryVersions: d.versions } };
      return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    });
  }

  /** Deletes the guest's data in this browser; the app goes back to sign-in. */
  override async deleteAccount(): Promise<void> {
    await this.current(async () => {
      this.storage?.removeItem(DATA_KEY);
      this.storage?.removeItem(MODE_KEY);
      this.ended = true;
    });
  }
}
